'use client';

/**
 * HighlightedJson — syntax-highlighted JSON for tool call/result payloads.
 *
 * Uses lowlight (the hast-emitting core of rehype-highlight) with ONLY the
 * JSON grammar registered, so it adds nothing beyond what the markdown
 * highlighting pipeline already ships. The hast tree is rendered to React
 * elements directly — no dangerouslySetInnerHTML on tool output.
 *
 * Token colors come from `code-highlight.css` (scoped under `.meridian-hljs`
 * on the surrounding <pre>), light + dark. The hast → React walker lives in
 * ./highlight-hast (shared with the artifact code viewer).
 */

import React, { useMemo } from 'react';
import { createLowlight } from 'lowlight';
import json from 'highlight.js/lib/languages/json';
import { renderHastNodes } from './highlight-hast.js';
import '../code-highlight.css';

const lowlight = createLowlight({ json });

export interface HighlightedJsonProps {
  /** Pretty-printed JSON text to highlight. */
  code: string;
}

export function HighlightedJson({ code }: HighlightedJsonProps) {
  const highlighted = useMemo(() => {
    const tree = lowlight.highlight('json', code);
    return renderHastNodes(tree.children, 'hljs');
  }, [code]);

  return <code className="hljs language-json font-mono">{highlighted}</code>;
}
